import React, { useEffect, useState } from "react";
import {
  ImageList,
  ImageListItem,
  Box,
  ImageListItemBar,
  TextField,
  Chip,
  Stack,
  imageListItemClasses,
} from "@mui/material";
import { useHistory, useParams } from "react-router-dom";
import useWindowDimensions from "../windowDimensions";
import { sportmap } from "../utilities/sportmap";
import athletesMostRecent from "../data/athletesMostRecentBackup.json";
import { nameToSlug } from "./PhotoUpload";

const CURRENT_YEAR = new Date().getFullYear();
const NO_PHOTO_URL = "https://plantbuilt.s3.us-west-2.amazonaws.com/profile-photos/plantbuilt-no-photo-square.png";

const AthleteImage = ({ athlete }) => {
  const squareSrc = athlete.img
    ? `${athlete.img.substring(0, athlete.img.length - 4)}-square.png`
    : null;
  const [src, setSrc] = useState(squareSrc || athlete.img || NO_PHOTO_URL);

  useEffect(() => {
    const sq = athlete.img
      ? `${athlete.img.substring(0, athlete.img.length - 4)}-square.png`
      : null;
    setSrc(sq || athlete.img || NO_PHOTO_URL);
  }, [athlete.img]);

  const handleError = () => {
    if (src === squareSrc && athlete.img) {
      setSrc(athlete.img);
    } else if (src !== NO_PHOTO_URL) {
      setSrc(NO_PHOTO_URL);
    }
  };

  return (
    <img
      className={imageListItemClasses.img}
      src={src}
      srcSet={src}
      alt={athlete.name}
      loading="lazy"
      id={athlete.name}
      onError={handleError}
    />
  );
};

const CHIPS = [
  { label: `${CURRENT_YEAR} Athletes`, filter: (a) => a.active },
  {
    label: "Bodybuilding",
    slug: "bodybuilding",
    filter: (a) =>
      (a.sport === "bodybuilding" || a.secondSport === "bodybuilding") && a.active,
  },
  {
    label: "CrossFit",
    slug: "cf",
    filter: (a) => (a.sport === "cf" || a.secondSport === "cf") && a.active,
  },
  {
    label: "Powerlifting",
    slug: "powerlifting",
    filter: (a) =>
      (a.sport === "powerlifting" || a.secondSport === "powerlifting") && a.active,
  },
  {
    label: "Strongman",
    slug: "strongman",
    filter: (a) =>
      (a.sport === "strongman" || a.secondSport === "strongman") && a.active,
  },
  {
    label: "Olympic Weightlifting",
    slug: "olympic-weightlifting",
    filter: (a) =>
      (a.sport === "olympic-weightlifting" || a.secondSport === "olympic-weightlifting") &&
      a.active,
  },
  {
    label: "Kettlebell Sport",
    slug: "kettle-bell-sport",
    filter: (a) =>
      (a.sport === "kettle-bell-sport" || a.secondSport === "kettle-bell-sport") &&
      a.active,
  },
  {
    label: "HYROX",
    slug: "hyrox",
    filter: (a) =>
      (a.sport === "hyrox" || a.secondSport === "hyrox") && a.active,
  },
  { label: "Legacy", slug: "former-team", filter: (a) => !a.active },
];

const Profiles = () => {
  const [athletesData, setAthletesData] = useState(
    athletesMostRecent.filter((a) => a.est !== 2024 && a.published !== false)
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedChip, setSelectedChip] = useState(0);
  const history = useHistory();
  const { sport } = useParams();
  const { width } = useWindowDimensions();

  useEffect(() => {
    (async () => {
      try {
        const data = await (
          await fetch(
            "https://plantbuilt.s3.us-west-2.amazonaws.com/athletes.json",
            { cache: "no-cache" }
          )
        ).json();
        if (data && data.length) {
          setAthletesData(data.filter((a) => a.est !== 2024 && a.published !== false));
        }
      } catch (e) {
        // keep backup data
      }
    })();
  }, []);

  // Pre-select chip from URL :sport param
  useEffect(() => {
    if (!sport) {
      setSelectedChip(0);
      return;
    }
    const idx = CHIPS.findIndex((c) => c.slug === sport);
    setSelectedChip(idx >= 0 ? idx : 0);
  }, [sport]);

  const filteredAthletes = athletesData
    .filter(CHIPS[selectedChip].filter)
    .filter(
      (a) =>
        !searchQuery ||
        a.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        marginTop: "20px",
        marginBottom: "20px",
      }}
    >
      <TextField
        label="Search athletes"
        variant="outlined"
        size="small"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ width: width * 0.8, marginBottom: "16px" }}
      />
      <Stack
        direction="row"
        sx={{
          width: width * 0.8,
          marginBottom: "16px",
          flexWrap: "wrap",
          gap: "8px",
        }}
      >
        {CHIPS.map((chip, idx) => (
          <Chip
            key={chip.label}
            label={chip.label}
            onClick={() => setSelectedChip(idx)}
            sx={
              selectedChip === idx
                ? { backgroundColor: "#709425", color: "white" }
                : {}
            }
          />
        ))}
      </Stack>
      <ImageList
        sx={{ width: width * 0.8, margin: "auto" }}
        cols={width > 800 ? 4 : width > 650 ? 3 : 1}
        rowHeight={
          width > 800
            ? (width * 0.8) / 4
            : width > 650
            ? (width * 0.8) / 3
            : width * 0.8
        }
      >
        {filteredAthletes.map((athlete) => (
          <ImageListItem
            key={athlete.name}
            onClick={() =>
              history.push("/athletes/" + nameToSlug(athlete.name))
            }
            style={{ cursor: "pointer" }}
          >
            <AthleteImage athlete={athlete} />
            <ImageListItemBar
              title={athlete.name}
              subtitle={
                width < 650
                  ? athlete.founder
                    ? "PlantBuilt Co-Founder"
                    : athlete.captain
                    ? `Team Captain - ${sportmap[athlete.sport]}`
                    : sportmap[athlete.sport]
                  : athlete.founder
                  ? "PlantBuilt Co-Founder"
                  : athlete.captain
                  ? "Team Captain"
                  : ""
              }
            />
          </ImageListItem>
        ))}
      </ImageList>
    </Box>
  );
};

export default Profiles;
